import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";

import palette from "../../../common/styles/palette";
import { AiAnalysis } from "../../../services/sheai/SheAiChecklistTypes";
import { AiCard, AiSectionHeader } from "./aiSurface";
import { TimestampChip } from "../debriefing-checklist/timestampLinks";

/**
 * A transcrição literal da simulação, produzida pelo agente ADK
 * (`audio_asr_agent`) e entregue em `analise.transcript`.
 *
 * Substitui o STT legado (`SttModule`/`SheAiSttService`), que transcrevia a
 * mesma mídia por um pipeline separado e divergia do que de fato foi falado.
 * Aqui a fonte é única: a mesma análise que alimenta Soft Skills e o Dossiê
 * fala a fala, com o mesmo contrato (`ts_start`/`ts_end` clicáveis).
 *
 * Sem transcript, a aba diz isso em uma linha em vez de um card vazio: a
 * transcrição nasce junto da análise, não é um segundo disparo.
 */

const ROTULO_FALANTE: Record<string, string> = {
  estudante: "Avaliado(a)",
  paciente: "Paciente",
  avaliador: "Avaliador",
};

const COR_FALANTE: Record<string, string> = {
  estudante: palette.brand,
  paciente: palette.emeraldDeep,
  avaliador: palette.lilacMid,
};

const falanteDe = (speaker: string | undefined): string =>
  ROTULO_FALANTE[String(speaker || "").toLowerCase()] || "Participante";

const corDe = (speaker: string | undefined): string =>
  COR_FALANTE[String(speaker || "").toLowerCase()] || palette.textMuted;

const AdkTranscriptSection: React.FC<{ analise?: AiAnalysis | null }> = ({
  analise,
}) => {
  const transcript = analise?.transcript || [];

  if (transcript.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: palette.textMuted }}>
        Transcrição ainda não disponível para esta gravação. Ela é gerada
        automaticamente junto com a análise de IA.
      </Typography>
    );
  }

  return (
    <AiCard>
      <AiSectionHeader
        icone={<MicNoneOutlinedIcon sx={{ color: palette.brand, mt: 0.25 }} />}
        titulo="Transcrição de áudio"
        subtitulo="Fala captada na gravação, transcrita literalmente pela análise de IA — com o instante de cada fala para pular o vídeo até lá."
      />

      <Box component="ul" sx={{ m: 0, p: 0, display: "grid", gap: 1, listStyle: "none" }}>
        {transcript.map((seg, idx) => {
          const ts = seg.ts_start || (typeof seg.start === "number" ? String(seg.start) : undefined);
          const texto = seg.text || seg.transcription || "";
          if (!texto.trim()) return null;

          return (
            <Box
              component="li"
              key={seg.id ?? idx}
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexWrap: "wrap",
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: palette.background,
                border: `1px solid ${palette.border}`,
              }}
            >
              <Chip
                label={falanteDe(seg.speaker)}
                size="small"
                sx={{
                  fontWeight: 700,
                  color: corDe(seg.speaker),
                  backgroundColor: `${corDe(seg.speaker)}1a`,
                  fontSize: "0.7rem",
                  height: 22,
                }}
              />
              {ts && <TimestampChip label={ts} ts={ts} />}
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 200, color: palette.text }}
              >
                {texto}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </AiCard>
  );
};

export default AdkTranscriptSection;
